import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, Facts, Field, Loading, Page } from "../components/ui";
import { text, when } from "../format";

function validAddress(value) {
  return /^0x[0-9a-fA-F]{40}$/.test(value.trim());
}

function validKey(value) {
  return /^[0-9a-fA-F]{64}$/.test(value.trim().replace(/^0x/i, ""));
}

export default function PayoutWallet() {
  const wallet = useFetch("/payout-wallet");
  const saved = wallet.data?.data;
  const exists = Boolean(saved && (saved.address || saved.hasPrivateKey));
  const [address, setAddress] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setAddress("");
    setPrivateKey("");
  }, [saved && saved.updatedAt]);

  async function onSubmit(event) {
    event.preventDefault();
    const nextErrors = {};
    const addressValue = address.trim();
    const keyValue = privateKey.trim();
    if (!exists && !addressValue) nextErrors.address = "BEP20 address is required";
    if (!exists && !keyValue) nextErrors.privateKey = "Private key is required";
    if (exists && !addressValue && !keyValue) nextErrors.privateKey = "Send an address or a private key to change";
    if (addressValue && !validAddress(addressValue)) nextErrors.address = "Enter a valid BEP20 address";
    if (keyValue && !validKey(keyValue)) nextErrors.privateKey = "Private key must be 64 hex characters";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setError("Validation failed");
      setNotice("");
      return;
    }

    const body = {};
    if (addressValue) body.address = addressValue;
    if (keyValue) body.privateKey = keyValue;
    setBusy(true);
    setError("");
    setErrors({});
    setNotice("");
    try {
      const payload = await api("/payout-wallet", { method: "PUT", body });
      setPrivateKey("");
      setAddress("");
      setNotice(payload.message || "Payout wallet saved");
      wallet.reload();
    } catch (err) {
      setError(errorText(err));
      setErrors(err.errors || {});
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page
      title="Payout wallet"
      text="One BEP20 wallet sends every automatic withdrawal. Members receive the site coin, usually USDT, from this address. The address needs BNB for gas, and BNBRPC must be set. The private key is stored and can be replaced. It is never shown again."
    >
      <Alerts error={wallet.error || error} notice={notice} />
      {wallet.loading && !saved ? <Loading /> : null}
      {saved ? (
        <>
          <div className="panel">
            <Facts items={[
              { label: "Chain", value: text(saved.chain || "BEP20") },
              { label: "Private key", value: <Badge value={saved.hasPrivateKey ? "saved" : "missing"} /> },
              { label: "Sending address", value: <span className="mono">{text(saved.address)}</span>, wide: true },
              { label: "Updated", value: when(saved.updatedAt) },
            ]} />
          </div>
          <form className="panel" onSubmit={onSubmit}>
            <h2 className="section-title">{exists ? "Replace wallet" : "Save wallet"}</h2>
            <div className="form-grid">
              <div className="wide">
                <Field label="BEP20 address" error={errors.address} hint={exists ? "Leave blank to keep the current address. 0x plus 40 hex characters." : "Required on the first save. 0x plus 40 hex characters."}>
                  <input className="mono" value={address} autoComplete="off" spellCheck="false" placeholder={saved.address || "0x"} onChange={(event) => setAddress(event.target.value)} />
                </Field>
              </div>
              <div className="wide">
                <Field label="Private key" error={errors.privateKey} hint={exists ? "Leave blank to keep the saved key. 64 hex characters. 0x is optional." : "Required on the first save. 64 hex characters. 0x is optional."}>
                  <input type="password" value={privateKey} autoComplete="new-password" spellCheck="false" onChange={(event) => setPrivateKey(event.target.value)} />
                </Field>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn" disabled={busy}>{busy ? "Saving..." : exists ? "Update payout wallet" : "Save payout wallet"}</button>
            </div>
          </form>
          <p className="hint">Withdrawals are sent from <Link to="/admin/withdrawals">Withdrawals</Link>. Only BEP20 member addresses are paid. Any other network is saved as failed.</p>
        </>
      ) : null}
    </Page>
  );
}
